import { useEffect, useState } from 'react';
import { MessageCircle, Quote, Play, X } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SectionHeading from '../components/SectionHeading';
import { FadeUp } from '../components/Reveal';
import { testimonials, videoTestimonials, images, waUrl, DEFAULT_WA_MESSAGE } from '../data/site';

function Stars({ rating }) {
  return (
    <div className="flex gap-1 text-gold" aria-label={`${rating} estrelas`} data-testid="testimonial-stars">
      {Array.from({ length: rating }).map((_, i) => (
        <svg key={i} width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2l2.9 6.26 6.6.7-4.9 4.5 1.35 6.54L12 16.9 6.05 20l1.35-6.54-4.9-4.5 6.6-.7L12 2z" />
        </svg>
      ))}
    </div>
  );
}

function VideoModal({ item, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-center bg-navy-deep/90 p-4 backdrop-blur-sm"
      data-testid="video-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl overflow-hidden rounded-2xl border border-white/15 bg-navy shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="video-modal-close"
          aria-label="Fechar vídeo"
          className="absolute right-3 top-3 z-10 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-gold hover:text-navy-deep"
        >
          <X size={18} />
        </button>
        {item.youtubeId ? (
          <div className="aspect-video w-full">
            <iframe
              className="h-full w-full"
              src={`https://www.youtube.com/embed/${item.youtubeId}?autoplay=1`}
              title={`Depoimento em vídeo — ${item.client}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : (
          <div className="relative aspect-video w-full">
            <img src={item.thumb} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
            <div className="absolute inset-0 grid place-items-center p-8 text-center">
              <div>
                <p className="font-display text-xl font-bold text-white md:text-2xl">
                  Espaço reservado para o depoimento em vídeo de {item.client}
                </p>
                <p className="mx-auto mt-3 max-w-md text-sm text-white/65">
                  O vídeo está em produção. Assim que publicado no YouTube, ele toca aqui automaticamente.
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="border-t border-white/10 p-5">
          <p className="font-display text-sm font-bold text-white">{item.client}</p>
          <p className="text-xs text-white/55">{item.role}</p>
        </div>
      </div>
    </div>
  );
}

export default function Depoimentos() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    document.body.style.overflow = active ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [active]);

  return (
    <>
      <PageHeader
        index="07"
        eyebrow="Depoimentos"
        title="A confiança de quem já construiu com a gente"
        description="Gestores de facilities, proprietários, síndicos, hoteleiros, varejo e arquitetos que receberam laudos, obras e regularizações com respaldo técnico completo."
        image={images.structure}
      />

      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-28" data-testid="testimonials-grid">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {testimonials.map((t, i) => (
            <FadeUp key={t.client} delay={(i % 3) * 0.1}>
              <article className="card-hover relative flex h-full flex-col rounded-2xl surface p-8" data-testid={`testimonial-full-card-${i}`}>
                <Quote size={36} className="text-gold/30" aria-hidden="true" />
                <Stars rating={t.rating} />
                <p className="mt-5 flex-1 leading-relaxed text-muted-c">“{t.text}”</p>
                <div className="mt-7 border-t border-line pt-5">
                  <p className="font-display text-base font-bold text-navy dark:text-white">{t.client}</p>
                  <p className="mt-1 text-xs text-muted-c">{t.role}</p>
                  <span className="chip mt-4">{t.service}</span>
                </div>
              </article>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="border-y border-line bg-[var(--surface)] py-24 lg:py-28" data-testid="video-testimonials">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading
            eyebrow="Depoimentos em Vídeo"
            title="Emotions e histórias contadas por quem viveu a obra"
            description="Assista aos relatos em vídeo dos clientes do Grupo Majo. Clique para abrir o player."
          />
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {videoTestimonials.map((v, i) => (
              <FadeUp key={v.client} delay={i * 0.1}>
                <button
                  onClick={() => setActive(v)}
                  data-testid={`video-card-${i}`}
                  className="group relative block w-full overflow-hidden rounded-2xl border border-line text-left shadow-[0_30px_70px_-35px_rgba(13,33,69,0.55)] transition-all duration-300 hover:-translate-y-1.5 hover:border-gold/50"
                >
                  <div className="relative aspect-video w-full overflow-hidden">
                    <img
                      src={v.thumb}
                      alt={`Depoimento em vídeo de ${v.client}`}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/25 to-transparent" aria-hidden="true" />
                    <span className="absolute inset-0 grid place-items-center" aria-hidden="true">
                      <span className="grid h-16 w-16 place-items-center rounded-full bg-gold text-navy-deep shadow-[0_0_0_10px_rgba(233,160,43,0.25)] transition-transform duration-300 group-hover:scale-110">
                        <Play size={24} fill="currentColor" className="ml-0.5" />
                      </span>
                    </span>
                    <div className="absolute bottom-4 left-5 right-5">
                      <p className="font-display text-base font-bold text-white">{v.client}</p>
                      <p className="text-xs text-white/65">{v.role}</p>
                    </div>
                  </div>
                  <div className="bg-[var(--surface)] p-4">
                    <span className="chip">{v.service}</span>
                  </div>
                </button>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-navy py-24 text-white" data-testid="testimonials-cta">
        <div className="absolute inset-0 bg-blueprint" aria-hidden="true" />
        <div className="relative mx-auto max-w-3xl px-5 text-center lg:px-8">
          <FadeUp>
            <h2 className="font-display text-3xl font-extrabold tracking-tight md:text-5xl">
              Seja o próximo cliente a garantir seu laudo com ART
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-white/65">
              Fale direto com o engenheiro responsável e receba uma proposta técnica sob medida.
            </p>
            <a href={waUrl(DEFAULT_WA_MESSAGE)} target="_blank" rel="noopener noreferrer" data-testid="testimonials-cta-whatsapp" className="btn-gold mt-9">
              <MessageCircle size={17} /> Chamar no WhatsApp
            </a>
          </FadeUp>
        </div>
      </section>

      {active && <VideoModal item={active} onClose={() => setActive(null)} />}
    </>
  );
}
